import * as Slider from '@radix-ui/react-slider'
import { Tabs } from 'antd'
import { Dropdown } from 'components/form/inputs/Dropdown'
import { useRolodexEdit } from 'features/fde'
import { useKestrel } from 'features/kestrelViewer'
import { useKestrelTutorialContext } from 'features/kestrelViewer/tutorial/KestrelTutorialContext'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import { useKestrelViewerContext } from 'features/kestrelViewer/types/KestrelViewerInterfaces'
import { PermissionsGate } from 'features/permissions'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import {
  DropdownOption,
  KestrelInspectionType,
  KVInputProfile,
  KVResolutionQuality,
  KVUnitType,
  PERMISSION_GECKO_INTERNAL_MEMBER,
} from 'types'

import { KestrelModal } from './tools/KestrelModal'

const ControlGroup = styled.div`
  margin-bottom: 0.75rem;
  text-align: left;
`

const ControlLabel = styled.div`
  font-size: 0.875rem;
  opacity: 0.95;
  letter-spacing: 0.05rem;
  margin-bottom: 0.25rem;
  font-weight: 500;
`

const SliderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const StyledSliderRoot = styled(Slider.Root)`
  position: relative;
  display: flex;
  align-items: center;
  user-select: none;
  touch-action: none;
  height: 20px;
  flex: 1;
`

const StyledSliderTrack = styled(Slider.Track)`
  background-color: rgba(255, 255, 255, 0.15);
  position: relative;
  border-radius: 9999px;
  height: 4px;
  flex: 1;
`

const StyledSliderRange = styled(Slider.Range)`
  position: absolute;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 9999px;
  height: 100%;
`

const StyledSliderThumb = styled(Slider.Thumb)`
  display: block;
  width: 12px;
  height: 20px;
  background-color: white;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.95);
    transform: scale(1.1);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
  }
`

const SliderValue = styled.div`
  min-width: 2.5rem;
  font-size: 0.875rem;
  text-align: right;
  color: rgba(255, 255, 255, 0.9);
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`

const UpdateButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  cursor: pointer;
  flex: 1;
  transition: all 0.2s ease;
  font-weight: 500;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.4);
  }

  &:active {
    background: rgba(255, 255, 255, 0.35);
    transform: translateY(1px);
  }
`

const SectionDivider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.15);
  margin: 1rem 0 0.75rem 0;
  border-radius: 9999px;
`

const StyledDropdownContainer = styled.div`
  .rs-picker-default .rs-picker-toggle.rs-btn {
    background-color: rgba(255, 255, 255, 0.15) !important;
    border-color: rgba(255, 255, 255, 0.2) !important;
    color: white !important;
    border-radius: 0.25rem !important;
  }

  .rs-picker-toggle-value {
    color: white !important;
    font-weight: 500 !important;
  }

  .rs-picker-toggle-placeholder {
    color: rgba(255, 255, 255, 0.6) !important;
  }

  .rs-btn:hover {
    background-color: rgba(255, 255, 255, 0.25) !important;
    border-color: rgba(255, 255, 255, 0.4) !important;
  }

  .rs-btn:focus {
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3) !important;
  }
`

const inputProfileOptions: DropdownOption[] = [
  { value: KVInputProfile.Default, label: 'Default' },
  { value: KVInputProfile.Switched, label: 'Switched' },
  { value: KVInputProfile.Creo, label: 'Creo' },
]

const inspectionTypeOptions: DropdownOption[] = [
  { value: KestrelInspectionType.DEFAULT, label: 'Default' },
  { value: KestrelInspectionType.ROOFTOP, label: 'Rooftop' },
  { value: KestrelInspectionType.SITEVIEW, label: 'Siteview' },
  { value: KestrelInspectionType.NAVY, label: 'Navy' },
  { value: KestrelInspectionType.G1N6, label: 'G1N6' },
  { value: KestrelInspectionType.AEROSPACE, label: 'Aerospace' },
]

export const Settings: React.FC = () => {
  const {
    sendCommand,
    settingsOpen,
    setSettingsOpen,
    settings,
    resolutionQuality,
    setResolutionQuality,
    currentUnit,
    setCurrentUnit,
  } = useKestrelViewerContext()
  const { kestrelModel, inspectionType } = useKestrel()
  const { rolodexPatch } = useRolodexEdit()
  const { setTutorialIndex, setShowTutorial } = useKestrelTutorialContext()

  // Local state for settings - initialized with context values
  const [fieldOfView, setFieldOfView] = useState(settings?.fieldOfView ?? 60)
  const [orbitSpeed, setOrbitSpeed] = useState(settings?.orbitSpeed ?? 0.7)
  const [inputProfile, setInputProfile] = useState<KVInputProfile>(
    settings?.inputProfile ?? KVInputProfile.Default
  )
  const [localResolutionQuality, setLocalResolutionQuality] =
    useState<KVResolutionQuality>(resolutionQuality)
  const [localCurrentUnit, setLocalCurrentUnit] =
    useState<KVUnitType>(currentUnit)
  const [selectedDefaultUnit, setSelectedDefaultUnit] = useState<
    import('types').KVUnitType | 'Not selected'
  >(kestrelModel?.data.defaultUnit ?? 'Not selected')
  const [measurementPrecision, setMeasurementPrecision] = useState<number>(3)
  const [cameraPosesScale, setCameraPosesScale] = useState<number>(
    kestrelModel?.data.cameraPosesScale ?? 0.01
  )
  const [selectedInspectionType, setSelectedInspectionType] =
    useState<KestrelInspectionType>(
      (inspectionType as KestrelInspectionType | undefined) ??
        KestrelInspectionType.DEFAULT
    )

  // Log scale mapping for 0.001 → 0.1 with 0.01 centered
  const LOG_MIN = -3
  const LOG_MAX = -1
  const STEPS = 10
  const scaleToIndex = (s: number) => {
    const t = (Math.log10(s) - LOG_MIN) / (LOG_MAX - LOG_MIN)
    return Math.round(Math.min(STEPS, Math.max(0, t * STEPS)))
  }
  const indexToScale = (i: number) => {
    const t = Math.min(1, Math.max(0, i / STEPS))
    return Math.pow(10, LOG_MIN + t * (LOG_MAX - LOG_MIN))
  }

  // Update local state when settings from context change
  useEffect(() => {
    if (settings) {
      setOrbitSpeed(settings.orbitSpeed)
      setFieldOfView(settings.fieldOfView)
      setInputProfile(settings.inputProfile)
    }
  }, [settings])

  useEffect(() => {
    setLocalResolutionQuality(resolutionQuality)
  }, [resolutionQuality])

  useEffect(() => {
    setLocalCurrentUnit(currentUnit)
  }, [currentUnit])

  // Keep local default unit in sync with model changes
  useEffect(() => {
    setSelectedDefaultUnit(kestrelModel?.data.defaultUnit ?? 'Not selected')
  }, [kestrelModel?.data.defaultUnit])

  useEffect(() => {
    setCameraPosesScale(kestrelModel?.data.cameraPosesScale ?? 0.01)
  }, [kestrelModel?.data.cameraPosesScale])

  useEffect(() => {
    setSelectedInspectionType(
      (inspectionType as KestrelInspectionType | undefined) ??
        KestrelInspectionType.DEFAULT
    )
  }, [inspectionType])

  if (!settingsOpen) return null

  const handleClose = () => {
    setSettingsOpen(false)
  }

  const handleUpdate = async () => {
    // Send commands to update the Kestrel viewer settings
    // Kestrel viewer will update the settings in the backend and send a settingsUpdated message to the client
    // The client will update the state in KV Context, which should be reflected in the hook above
    sendCommand({
      command: KestrelCommands.UPDATE_SETTINGS,
      settings: {
        orbitSpeed: orbitSpeed,
        fieldOfView: fieldOfView,
        inputProfile: inputProfile.toString(),
      },
    })

    setResolutionQuality(localResolutionQuality)

    // Apply current measurement unit
    try {
      setCurrentUnit(localCurrentUnit)
      sendCommand({
        command: KestrelCommands.CHANGE_MEASUREMENT_UNIT,
        measurementUnit: String(localCurrentUnit).toLowerCase(),
      })
    } catch (error) {
      console.error('Failed to update current unit:', error)
    }

    // Apply measurement precision
    try {
      sendCommand({
        command: KestrelCommands.SET_MEASUREMENT_POSITION,
        precision: measurementPrecision,
      })
    } catch (error) {
      console.error('Failed to update measurement precision:', error)
    }

    // Persist defaultUnit to rolodex without changing currentUnit
    try {
      if (kestrelModel?.id) {
        const data: Record<string, unknown> = {
          cameraPosesScale: cameraPosesScale,
        }
        if (selectedDefaultUnit !== 'Not selected') {
          data.defaultUnit = selectedDefaultUnit
        }
        data.inspection_type = selectedInspectionType
        if (Object.keys(data).length > 0) {
          const patchEntry = { id: kestrelModel.id, data }
          await rolodexPatch([patchEntry])
        }
      }
    } catch (error) {
      console.error('Failed to update default unit in rolodex:', error)
    }

    // Close the settings popup after updating
    handleClose()
  }

  return (
    <KestrelModal title='Settings' handleClose={handleClose}>
      <Tabs
        defaultActiveKey='1'
        tabBarStyle={{ display: 'flex' }} // Ensure the tab bar is a flex container
        renderTabBar={(props, DefaultTabBar) => <DefaultTabBar {...props} />}
        items={[
          {
            key: '1',
            label: 'User',
            children: (
              <>
                <ControlGroup>
                  <ControlLabel>Current Units</ControlLabel>
                  <StyledDropdownContainer>
                    <Dropdown
                      options={[
                        { value: 'Meters', label: 'Meters' },
                        { value: 'Millimeters', label: 'Millimeters' },
                        { value: 'Feet', label: 'Feet' },
                        { value: 'Inches', label: 'Inches' },
                      ]}
                      onValueChange={(value: string) =>
                        setLocalCurrentUnit(value as KVUnitType)
                      }
                      defaultValue={localCurrentUnit}
                      placeholder='Select current units'
                      $backgroundColor='rgba(255, 255, 255, 0.1)'
                      $borderColor='rgba(255, 255, 255, 0.2)'
                      searchable={false}
                      cleanable={false}
                    />
                  </StyledDropdownContainer>
                </ControlGroup>
                <ControlGroup>
                  <ControlLabel>Resolution Quality</ControlLabel>
                  <StyledDropdownContainer>
                    <Dropdown
                      options={[
                        {
                          value: String(KVResolutionQuality.Low),
                          label: 'Low',
                        },
                        {
                          value: String(KVResolutionQuality.Standard),
                          label: 'Medium',
                        },
                        {
                          value: String(KVResolutionQuality.High),
                          label: 'High',
                        },
                      ]}
                      onValueChange={(value: string) =>
                        setLocalResolutionQuality(
                          Number(value) as KVResolutionQuality
                        )
                      }
                      defaultValue={String(localResolutionQuality)}
                      placeholder='Select resolution quality'
                      $backgroundColor='rgba(255, 255, 255, 0.1)'
                      $borderColor='rgba(255, 255, 255, 0.2)'
                      searchable={false}
                      cleanable={false}
                    />
                  </StyledDropdownContainer>
                </ControlGroup>
                <ControlGroup>
                  <ControlLabel>Measurement Precision</ControlLabel>
                  <SliderContainer>
                    <StyledSliderRoot
                      min={0}
                      max={6}
                      step={1}
                      value={[measurementPrecision]}
                      onValueChange={(value) =>
                        setMeasurementPrecision(value[0])
                      }>
                      <StyledSliderTrack>
                        <StyledSliderRange />
                      </StyledSliderTrack>
                      <StyledSliderThumb />
                    </StyledSliderRoot>
                    <SliderValue>
                      {measurementPrecision === 0
                        ? '1'
                        : `0.${'0'.repeat(measurementPrecision - 1)}1`}
                    </SliderValue>
                  </SliderContainer>
                </ControlGroup>
                <ControlGroup>
                  <ControlLabel>Default Units</ControlLabel>
                  <StyledDropdownContainer>
                    <Dropdown
                      options={[
                        { value: 'Not selected', label: 'Not selected' },
                        { value: 'Meters', label: 'Meters' },
                        { value: 'Millimeters', label: 'Millimeters' },
                        { value: 'Feet', label: 'Feet' },
                        { value: 'Inches', label: 'Inches' },
                      ]}
                      onValueChange={(value: string) =>
                        setSelectedDefaultUnit(
                          value as import('types').KVUnitType | 'Not selected'
                        )
                      }
                      defaultValue={selectedDefaultUnit}
                      placeholder='Select default units'
                      $backgroundColor='rgba(255, 255, 255, 0.1)'
                      $borderColor='rgba(255, 255, 255, 0.2)'
                      searchable={false}
                      cleanable={false}
                    />
                  </StyledDropdownContainer>
                </ControlGroup>
                <ControlGroup>
                  <ControlLabel>Orbit Speed</ControlLabel>
                  <SliderContainer>
                    <StyledSliderRoot
                      min={0.1}
                      max={2.0}
                      step={0.1}
                      value={[orbitSpeed]}
                      onValueChange={(value) => setOrbitSpeed(value[0])}>
                      <StyledSliderTrack>
                        <StyledSliderRange />
                      </StyledSliderTrack>
                      <StyledSliderThumb />
                    </StyledSliderRoot>
                    <SliderValue>{orbitSpeed.toFixed(1)}</SliderValue>
                  </SliderContainer>
                </ControlGroup>

                <ControlGroup>
                  <ControlLabel>Field of View</ControlLabel>
                  <SliderContainer>
                    <StyledSliderRoot
                      min={20}
                      max={120}
                      step={1}
                      value={[fieldOfView]}
                      onValueChange={(value) => setFieldOfView(value[0])}>
                      <StyledSliderTrack>
                        <StyledSliderRange />
                      </StyledSliderTrack>
                      <StyledSliderThumb />
                    </StyledSliderRoot>
                    <SliderValue>{fieldOfView}</SliderValue>
                  </SliderContainer>
                </ControlGroup>

                <ControlGroup>
                  <ControlLabel>Input Profile</ControlLabel>
                  <StyledDropdownContainer>
                    <Dropdown
                      options={inputProfileOptions}
                      onValueChange={(value: string) =>
                        setInputProfile(value as KVInputProfile)
                      }
                      defaultValue={inputProfile}
                      placeholder='Select input profile'
                      $backgroundColor='rgba(255, 255, 255, 0.1)'
                      $borderColor='rgba(255, 255, 255, 0.2)'
                      searchable={false}
                      cleanable={false}
                    />
                  </StyledDropdownContainer>
                </ControlGroup>
                <ControlGroup>
                  <ControlLabel>View Tutorial</ControlLabel>
                  <ButtonContainer>
                    <UpdateButton
                      onClick={() => {
                        setTutorialIndex(0)
                        setShowTutorial(true)
                        handleClose()
                      }}>
                      View Tutorial
                    </UpdateButton>
                  </ButtonContainer>
                </ControlGroup>
              </>
            ),
          },
          {
            key: '2',
            label: 'Scene',
            children: (
              <>
                <ControlGroup>
                  <ControlLabel>Camera Poses Scale</ControlLabel>
                  <SliderContainer>
                    <StyledSliderRoot
                      min={0}
                      max={STEPS}
                      step={1}
                      value={[scaleToIndex(cameraPosesScale)]}
                      onValueChange={(value) => {
                        const idx = Number(value[0])
                        const newScale = indexToScale(idx)
                        setCameraPosesScale(newScale)
                        sendCommand({
                          command: KestrelCommands.SET_CAMERA_POSES_SCALE,
                          cameraPosesScale: newScale,
                        })
                      }}>
                      <StyledSliderTrack>
                        <StyledSliderRange />
                      </StyledSliderTrack>
                      <StyledSliderThumb />
                    </StyledSliderRoot>
                    <SliderValue>
                      {(cameraPosesScale / 0.01).toFixed(2)}
                    </SliderValue>
                  </SliderContainer>
                </ControlGroup>

                <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
                  <ControlGroup>
                    <ControlLabel>
                      Inspection Type (refresh required)
                    </ControlLabel>
                    <StyledDropdownContainer>
                      <Dropdown
                        options={inspectionTypeOptions}
                        onValueChange={(value: string) =>
                          setSelectedInspectionType(
                            value as KestrelInspectionType
                          )
                        }
                        defaultValue={selectedInspectionType}
                        placeholder='Select inspection type'
                        $backgroundColor='rgba(255, 255, 255, 0.1)'
                        $borderColor='rgba(255, 255, 255, 0.2)'
                        searchable={false}
                        cleanable={false}
                      />
                    </StyledDropdownContainer>
                  </ControlGroup>
                </PermissionsGate>

                <ControlGroup>
                  <ControlLabel>Starting View</ControlLabel>
                  <ButtonContainer>
                    <UpdateButton
                      onClick={() => {
                        sendCommand({
                          command: KestrelCommands.CREATE_STARTING_VIEW,
                        })
                        handleClose()
                      }}>
                      Save Current View
                    </UpdateButton>
                  </ButtonContainer>
                </ControlGroup>
              </>
            ),
          },
        ]}
      />
      <SectionDivider />
      <ButtonContainer>
        <UpdateButton onClick={handleUpdate}>Update</UpdateButton>
      </ButtonContainer>
    </KestrelModal>
  )
}
